/**
 * CORE-1717 — несколько равноправных ведущих одной Live-сессии.
 *
 * Прежняя модель: один ведущий (`room.hostUserId`) и соведущие с урезанным
 * делегированным набором (CORE-552/851). Новая: ведущих сколько угодно, у всех
 * одинаковые инструменты в пределах возможностей самой сессии. Сервер проверяет
 * права всех ведущих; этот модуль — клиентская половина того же правила, без DOM,
 * чтобы её можно было проверить тестами.
 *
 *   - `hostUserIds` в снимке комнаты — весь состав: первый ведущий (тот, кто
 *     ведёт по праву создателя), затем назначенные в порядке принятия. На
 *     старом бэкенде списка нет — тогда ведущий это `hostUserId` или `isCohost`.
 *   - Назначение — предложение с принятием (`HostInvite`); права появляются
 *     только после «Принять».
 *   - Авторство не двигается никогда: анонс и записи остаются у
 *     `creatorUserId` (CORE-1328), что бы ни делали ведущие.
 */
import type { ApiError, HostInvite, RoomResponse } from '../types/auth';

type RoomHostFields = Pick<RoomResponse, 'hostUserId'> & {
    isCohost?: boolean;
    hostUserIds?: string[] | null;
};

/** Состав ведущих из снимка комнаты. Первый — всегда `hostUserId`. */
export function roomHostIds(room: RoomHostFields | null | undefined, selfUserId?: string | null): string[] {
    if (!room) return [];
    if (Array.isArray(room.hostUserIds) && room.hostUserIds.length > 0) {
        const ids = room.hostUserIds.filter(Boolean);
        return ids[0] === room.hostUserId ? ids : [room.hostUserId, ...ids.filter(id => id !== room.hostUserId)];
    }
    // Старый бэкенд: о других ведущих снимок не знает, о себе — знает.
    const out = [room.hostUserId];
    if (room.isCohost && selfUserId && selfUserId !== room.hostUserId) out.push(selfUserId);
    return out;
}

/** Ведущий ли я этой сессии — первый или назначенный. */
export function isSessionHost(room: RoomHostFields | null | undefined, selfUserId: string | null | undefined): boolean {
    if (!room || !selfUserId) return false;
    if (room.hostUserId === selfUserId || room.isCohost === true) return true;
    return Array.isArray(room.hostUserIds) && room.hostUserIds.includes(selfUserId);
}

/** Присутствующий в комнате: id и, с CORE-1782, умеет ли его клиент публиковать практику. */
export type PresentParticipant = string | { id: string; canPublishPractice?: boolean | null };

/**
 * Кто из ведущих публикует практику в канал для записи. Uid практики у комнаты
 * один, и два публикующих выбивали бы друг друга из канала, поэтому выбирается
 * ровно один: первый по составу ведущий, который сейчас в комнате. Все клиенты
 * считают одинаково, потому что состав и ростер у всех одни.
 *
 * CORE-1782 — и который умеет публиковать: ведущий, у которого в ростере
 * `canPublishPractice === false` (сегодня — приложение iOS/Android),
 * пропускается. Иначе ведущий с телефона, первый по составу, «занимал» место
 * публикующего, веб второго ведущего молчал, и в записи не было видео практики.
 * Нет поля (старый бэкенд, строка без признака) — ведущий годится, как раньше.
 */
export function practicePublisherId(
    hostIds: readonly string[],
    present: Iterable<PresentParticipant>,
): string | null {
    const eligible = new Set<string>();
    for (const p of present) {
        if (typeof p === 'string') eligible.add(p);
        else if (p.canPublishPractice !== false) eligible.add(p.id);
    }
    return hostIds.find(id => eligible.has(id)) ?? null;
}

/** Остаются ли в комнате другие ведущие, если я выйду. */
export function otherHostsPresent(
    hostIds: readonly string[],
    presentIds: Iterable<string>,
    selfUserId: string | null | undefined,
): boolean {
    const present = new Set(presentIds);
    return hostIds.some(id => id !== selfUserId && present.has(id));
}

/** Гость по ссылке (без аккаунта) ведущим не становится: ему нужно войти. */
export function isAnonymousParticipantId(userId: string | null | undefined): boolean {
    return !userId || userId.startsWith('anon-');
}

/**
 * Кадр `CohostsChanged` с CORE-1717 несёт полный состав (`hostUserIds`).
 * Состав без списка (старый бэкенд) ничего не меняет — возвращается прежний.
 */
export function hostIdsAfterFrame(
    prev: readonly string[],
    frame: { hostUserIds?: unknown } | null | undefined,
): readonly string[] {
    const ids = frame?.hostUserIds;
    if (!Array.isArray(ids) || !ids.every(id => typeof id === 'string')) return prev;
    if (ids.length === prev.length && ids.every((id, i) => prev[i] === id)) return prev;
    return ids as string[];
}

/**
 * Свежее ли это предложение мне: ожидает ответа, адресовано мне и не истекло.
 * Устаревшее предложение не показываем — сервер его всё равно не примет.
 */
export function isActionableHostInvite(
    invite: HostInvite | null | undefined,
    selfUserId: string | null | undefined,
    nowMs: number = Date.now(),
): invite is HostInvite {
    if (!invite || !selfUserId) return false;
    if (invite.status !== 'pending' || invite.targetUserId !== selfUserId) return false;
    const expires = Date.parse(invite.expiresAtUtc);
    return Number.isNaN(expires) || expires > nowMs;
}

/**
 * Список ожидающих предложений после кадра `HostInviteChanged`: ожидающее
 * добавляется (или обновляется), любой ответ его убирает.
 */
export function pendingInvitesAfterFrame(
    prev: readonly HostInvite[],
    invite: HostInvite | null | undefined,
): readonly HostInvite[] {
    if (!invite || typeof invite.id !== 'number') return prev;
    const rest = prev.filter(i => i.id !== invite.id && i.targetUserId !== invite.targetUserId);
    if (invite.status === 'pending') return [...rest, invite];
    return rest.length === prev.length ? prev : rest;
}

export type HostInviteFailure =
    | 'registration_required'
    | 'already_host'
    | 'not_present'
    | 'not_pending'
    | 'stage_full'
    | 'session_ended'
    /** CORE-1829 — у сессии нет `session.cohosts` (сессия не мастера). */
    | 'not_allowed'
    | 'failed';

/** Отказ сервера на приглашение или принятие — в понятную причину. */
export function describeHostInviteFailure(err: unknown): HostInviteFailure {
    const code = (err as ApiError | null)?.code;
    switch (code) {
        case 'host_registration_required': return 'registration_required';
        case 'already_host': return 'already_host';
        case 'not_present': return 'not_present';
        case 'host_invite_not_pending': return 'not_pending';
        case 'stage_full': return 'stage_full';
        case 'session_ended': return 'session_ended';
        case 'capability_required': return 'not_allowed';
        default: return 'failed';
    }
}

/** i18n-ключ под `room.hosts.errors` для отказа. */
export function hostInviteFailureKey(failure: HostInviteFailure): string {
    return `room.hosts.errors.${failure}`;
}
